export type PopupContextSource =
  | 'opener_postmessage'
  | 'storage_relay'
  | 'mobile_redirect';

export type PopupContextEnvelope = {
  source: PopupContextSource;
  payload: {
    waba_id?: string;
    phone_number_id?: string;
    business_id?: string;
  } | null;
};

const KEY_PREFIX = 'meta_popup_context:';

export function writePopupContext(state: string, env: PopupContextEnvelope): void {
  sessionStorage.setItem(KEY_PREFIX + state, JSON.stringify(env));
}

export function readPopupContext(state: string): PopupContextEnvelope | null {
  const raw = sessionStorage.getItem(KEY_PREFIX + state);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PopupContextEnvelope;
  } catch {
    return null;
  }
}

export function clearPopupContext(state: string): void {
  sessionStorage.removeItem(KEY_PREFIX + state);
}

// Pending-state registry (per-tab, sessionStorage). The localStorage relay in
// useMetaSignupListeners fires in EVERY same-origin tab; without this gate,
// every open app tab would race to navigate and consume the same one-shot
// OAuth completion (loser tabs 409 on META_OAUTH_STATE_CONSUMED). Launchers
// record the minted state here so only the tab that started the flow reacts.
const PENDING_KEY = 'meta_signup_pending_states';
const PENDING_CAP = 5;

export function recordPendingState(state: string): void {
  const states = readPendingStates();
  sessionStorage.setItem(
    PENDING_KEY,
    JSON.stringify([...states.filter((s) => s !== state), state].slice(-PENDING_CAP)),
  );
}

export function isPendingState(state: string): boolean {
  return readPendingStates().includes(state);
}

function readPendingStates(): string[] {
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(PENDING_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === 'string') : [];
  } catch {
    return [];
  }
}
